import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  IconButton,
  InputAdornment,
  SvgIcon,
  Typography,
} from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ConfirmationMessageModal,
  Modal,
  ModalAlert,
  FullScreenLoader,
  TextFieldWrapper,
  ScrollToTop,
  TextFieldWrapperWithInputMask,
  SelectFieldWrapper,
} from '@Components/index';
import { CommonConstants, Icons } from '@Constants/index';
import {
  useUpdateGlobalDncToResyncMutation,
  useUpdatePersonalDncToResyncMutation,
} from '@Services/dataIntegration.service';
import {
  useAddDncRowMutation,
  useEditDncRowMutation,
  useGetDncQuery,
} from '@Services/dnc.service';
import {
  useAddGlobalDncRowMutation,
  useEditGlobalDncRowMutation,
  useGetGlobalDncQuery,
} from '@Services/globalDnc.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  PhoneDispositions,
} from '@Typings/global.types';
import {
  DNCSelectOptions,
  getConfirmationMessageNode,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useTokenAccIdValueHook } from '@Utils/hooks/useTokenValues.hook';

import { DncModalInputs, DncModalProps, DncModalData } from './DncModal.types';
import DncModalSchema from './DncModalSchema';

const DncModal: React.FunctionComponent<DncModalProps> = (
  props: DncModalProps
): JSX.Element => {
  /** Hooks */
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  /** Props} */
  const {
    dncId,
    phoneNumber,
    crudType,
    isGlobal,
    setCrudType,
    deleteBtnOnClick,
  } = props;

  /** States */
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [modalAlertState, setModalAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  /** FORM METHODS */
  const methods = useForm<DncModalInputs>({
    resolver: yupResolver(DncModalSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /** Returns Modal Header Text */
  const getHeaderText = (): string => {
    switch (crudType) {
      case DialerCrudTypes.Add: {
        return isGlobal
          ? t('dnc.New Global DNC Number')
          : t('dnc.New DNC Number');
      }
      case DialerCrudTypes.Edit: {
        return isGlobal ? t('dnc.Edit Global Number') : t('dnc.Edit Number');
      }
      case DialerCrudTypes.View: {
        return isGlobal ? t('dnc.Global Number View') : t('dnc.Number View');
      }
      default: {
        return '';
      }
    }
  };

  /** AccountId for current user */
  const accountId =
    (query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) as number) ||
    undefined;

  const accountIdFromToken = useTokenAccIdValueHook() || 0;

  /** RTK Query Hooks for DNC */
  const [editDncRow, editDncMethods] = useEditDncRowMutation();
  const [addDncRow, addDncMethods] = useAddDncRowMutation();
  const [updatePersonalDncToResync] = useUpdatePersonalDncToResyncMutation();
  const [updateGlobalDncToResync] = useUpdateGlobalDncToResyncMutation();
  const {
    data: dncData,
    isLoading,
    isFetching,
    error: dncModalError,
  } = useGetDncQuery(
    { dncId, accountId },
    {
      skip: isGlobal || !dncId,
    }
  );

  /** RTK Query Hooks for Global DNC */
  const [editGlobalDncRow, editGlobalDncMethods] =
    useEditGlobalDncRowMutation();
  const [addGlobalDncRow, addGlobalDncMethods] = useAddGlobalDncRowMutation();
  const {
    data: globalDncData,
    isLoading: globalDncLoading,
    isFetching: globalDncFetching,
    error: globalDncModalError,
  } = useGetGlobalDncQuery(dncId, {
    skip: !isGlobal || !dncId || !crudType,
  });

  /** Handle Data */
  const data = isGlobal ? globalDncData?.items[0] : dncData?.items[0];

  /** ReadOnly prop for form fields if CRUD Type is View or Archive View */
  const readOnly =
    crudType === DialerCrudTypes.View || crudType === DialerCrudTypes.Archive;

  /** Select options */
  const options = DNCSelectOptions();

  /** Loading State */
  const loading =
    isLoading ||
    isFetching ||
    editDncMethods.isLoading ||
    addDncMethods.isLoading ||
    globalDncLoading ||
    globalDncFetching ||
    editGlobalDncMethods.isLoading ||
    addGlobalDncMethods.isLoading;

  /** Toggle Modal State
   * We are using useCallback as it returns memoized version of function
   * It will not cause infinite render of this component if used in useEffect.
   */
  const handleModalClose = useCallback(() => {
    setCrudType(null);
    setModalAlertState({ openAlert: false });
    methods.unregister();
  }, [methods, setCrudType]);

  /** SUCCESS AND ERROR ALERT HANDLERS --> */
  useEffect(() => {
    // QUERY ERROR
    if (dncModalError) {
      handleModalClose();
      triggerAlert(dncModalError, setAlertState);
    }

    // GLOBAL QUERY ERROR
    if (globalDncModalError) {
      handleModalClose();
      triggerAlert(globalDncModalError, setAlertState);
    }
  }, [dncModalError, handleModalClose, globalDncModalError, triggerAlert]);

  useEffect(() => {
    // ADD NUMBER ERROR
    if (addDncMethods.error) {
      if ((addDncMethods.error as FetchBaseQueryError).status === 409) {
        methods.setValue('dncNumber', '');
      }
      triggerAlert(addDncMethods.error, setModalAlertState);
    }

    // ADD NUMBER SUCCESS
    if (addDncMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.DNC_ADD,
        setAlertState,
        'success'
      );
      handleModalClose();
    }

    // ADD GLOBAL NUMBER ERROR
    if (addGlobalDncMethods.error) {
      if ((addGlobalDncMethods.error as FetchBaseQueryError).status === 409) {
        methods.setValue('dncNumber', '');
      }
      triggerAlert(addGlobalDncMethods.error, setModalAlertState);
    }

    // ADD GLOBAL NUMBER SUCCESS
    if (addGlobalDncMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_ADD,
        setAlertState,
        'success'
      );
      handleModalClose();
    }
  }, [
    methods,
    addDncMethods.error,
    addDncMethods.isSuccess,
    addGlobalDncMethods.error,
    addGlobalDncMethods.isSuccess,
    handleModalClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // EDIT NUMBER ERROR
    if (editDncMethods.error) {
      triggerAlert(editDncMethods.error, setModalAlertState);
    }

    // EDIT NUMBER SUCCESS
    if (editDncMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.DNC_EDIT,
        setAlertState,
        'success'
      );
      handleModalClose();
    }

    // EDIT GLOBAL NUMBER ERROR
    if (editGlobalDncMethods.error) {
      triggerAlert(editGlobalDncMethods.error, setModalAlertState);
    }

    // EDIT GLOBAL NUMBER SUCCESS
    if (editGlobalDncMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_EDIT,
        setAlertState,
        'success'
      );
      handleModalClose();
    }
  }, [
    editDncMethods.isSuccess,
    editDncMethods.error,
    editGlobalDncMethods.isSuccess,
    editGlobalDncMethods.error,
    handleModalClose,
    triggerAlert,
  ]);
  /** <-- Handle Error Alert */
  /** <-- SUCCESS AND ERROR ALERT HANDLERS */

  /** FORM HANDLERS --> */
  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<DncModalData> = async (formData) => {
    if (CommonConstants.REGEX.PHONE_NUMBER.exec(formData.dncNumber)) {
      const unformattedPhone = unformatUSNumber(formData.dncNumber.trim());

      if (crudType === DialerCrudTypes.Add) {
        if (isGlobal) {
          await addGlobalDncRow({
            phone: unformattedPhone,
            reason: formData.reason.value,
            comments: formData.note.trim(),
          });
          updateGlobalDncToResync({
            phone: unformattedPhone,
            prevPhone: null,
          }).catch((error) => {
            console.log(
              'Error with global add request to Resync service: ',
              error
            );
          });
        } else {
          await addDncRow({
            phone: unformattedPhone,
            reason: formData.reason.value,
            comments: formData.note.trim(),
            accountId,
          });

          updatePersonalDncToResync({
            accountId: accountIdFromToken,
            phones: [unformattedPhone],
            dispositionName: PhoneDispositions.DO_NOT_CALL,
          }).catch((error) => {
            console.log('Error with add request to Resync service:', error);
          });
        }
      }
      if (crudType === DialerCrudTypes.Edit) {
        const previousPhone = phoneNumber;
        const newPhone = unformattedPhone;

        if (isGlobal) {
          await editGlobalDncRow({
            id: dncId,
            phone: unformattedPhone,
            reason: formData.reason.value,
            comments: formData.note.trim(),
          });

          if (previousPhone !== newPhone) {
            updateGlobalDncToResync({
              phone: unformattedPhone,
              prevPhone: previousPhone,
            }).catch((error) => {
              console.log(
                'Error with global add request to Resync service: ',
                error
              );
            });
          }
        } else {
          await editDncRow({
            id: dncId,
            phone: unformattedPhone,
            reason: formData.reason.value,
            comments: formData.note.trim(),
            accountId,
          });

          if (previousPhone !== newPhone) {
            await updatePersonalDncToResync({
              accountId: accountIdFromToken,
              phones: [previousPhone],
              dispositionName: PhoneDispositions.NONE,
            });
            await updatePersonalDncToResync({
              accountId: accountIdFromToken,
              phones: [newPhone],
              dispositionName: PhoneDispositions.DO_NOT_CALL,
            });
          }
        }
      }
    } else {
      methods.setError('dncNumber', {
        message: 'Number is invalid',
      });
    }
  };

  /** Return Suitable Button for crudType */
  const getRestButton = () => {
    switch (crudType) {
      case DialerCrudTypes.View:
        return (
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.preventDefault();
              setCrudType(DialerCrudTypes.Edit);
            }}
          >
            {t('common.messages.Edit')}
          </Button>
        );
      default:
        return (
          <Button type="submit" onClick={methods.handleSubmit(onSubmit)}>
            {t('common.messages.Save')}
          </Button>
        );
    }
  };

  /**
   * View CRUD, Edit CRUD - Resets Form Fields with the API DATA
   * Add CRUD - Empty the Form Fields
   */
  useEffect(() => {
    if (data && crudType !== DialerCrudTypes.Add) {
      methods.reset({
        dncNumber: data.phone,
        reason: {
          label: data.reason,
          value: data.reason,
        },
        note: data.comments?.trim() || '',
      });
    } else if (crudType === DialerCrudTypes.Add) {
      methods.reset({
        dncNumber: '',
        note: '',
        reason: {
          label: undefined,
          value: undefined,
        },
      });
    }
  }, [methods, data, crudType]);

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (dncId && deleteBtnOnClick) {
      handleModalClose();
      return deleteBtnOnClick(dncId);
    }
  };

  return (
    <>
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(ConfirmationActionTypes.Delete)}
        onClick={handleConfirmationModalClick}
      />
      <Modal
        open={!!crudType}
        onClose={handleModalClose}
        headerText={getHeaderText()}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            role="form"
            sx={{
              position: 'relative',
              maxHeight: '68vh',
              padding: '3rem 2.4rem 2.4rem',
              overflow: loading ? 'hidden' : 'auto',
            }}
          >
            <ScrollToTop dependencies={[loading]} />
            {loading ? (
              <FullScreenLoader
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'common.white',
                  zIndex: 101,
                }}
              />
            ) : (
              <ModalAlert alertState={modalAlertState} />
            )}
            <TextFieldWrapperWithInputMask
              name="dncNumber"
              fullWidth
              placeholder={t('dnc.Type Number')}
              label={`${t('dnc.Number')} *`}
              mask={CommonConstants.PHONE_NUMBER_INPUT_MASK}
              sx={{
                maxWidth: '51.2rem',
                mt: modalAlertState.openAlert ? 7 : undefined,
              }}
              InputProps={{
                endAdornment: crudType !== DialerCrudTypes.View &&
                  methods.getValues('dncNumber') && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          methods.setValue('dncNumber', '', {
                            shouldValidate: true,
                          });
                        }}
                      >
                        <SvgIcon>{Icons.close}</SvgIcon>
                      </IconButton>
                    </InputAdornment>
                  ),
                readOnly: crudType === DialerCrudTypes.View,
              }}
            />
            <Box display="flex" mt={3.8}>
              <SelectFieldWrapper
                name="reason"
                options={options}
                multiple={false}
                textFieldProps={{
                  label: `${t('dnc.Reason')} *`,
                  placeholder: t('dnc.Select Reason'),
                  InputProps: {
                    readOnly,
                  },
                }}
                sx={{ minWidth: '24.8rem', maxWidth: '24.8rem', mr: 5 }}
              />
            </Box>
            <Box mt={3.2} mb={2.4}>
              <Typography
                mb={1.6}
                variant="main"
                color={methods.formState.errors.note ? 'error.main' : ''}
                fontWeight="bold"
              >
                {`${t('common.messages.Note')} *`}
              </Typography>
              <TextFieldWrapper
                name="note"
                multiline
                rows={7}
                fullWidth
                placeholder={t('dnc.Type your note')}
                sx={{
                  '& textarea': { padding: 0 },
                }}
                InputProps={{
                  readOnly: crudType === DialerCrudTypes.View,
                }}
              />
            </Box>
            <Box display="flex" alignItems="center">
              {crudType !== DialerCrudTypes.Add && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setIsConfirmationModalOpen(true);
                  }}
                >
                  {t('common.messages.Delete')}
                </Button>
              )}
              <Button
                variant="text"
                color="secondary"
                onClick={() => setCrudType(null)}
                sx={{ ml: 'auto' }}
              >
                {t('common.messages.Cancel')}
              </Button>
              {getRestButton()}
            </Box>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};
export default DncModal;
